import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Grid,
  MenuItem,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { getSuppliers } from '../../collections/suppliers';
import PurchaseOrder from '../../models/purchaseOrder';

const PurchaseOrderForm = ({ onSubmit, initialData = null, isEdit = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [purchaseOrder, setPurchaseOrder] = useState(new PurchaseOrder(initialData || {}));
  const [suppliers, setSuppliers] = useState([]);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [supplierProductsData, setSupplierProductsData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Load suppliers on component mount (products are included in supplier response)
  useEffect(() => {
    const loadData = async () => {
      try {
        const suppliersData = await getSuppliers({ per_page: 200, include_products: true });

        setSuppliers(suppliersData.data || []);

        // If editing and supplier is already selected, set supplier products
        if (isEdit && initialData?.supplier_id) {
          const selectedSupplier = (suppliersData.data || []).find(s => s.id === initialData.supplier_id);

          let supplierProductsArray = [];
          if (selectedSupplier?.products?.items && Array.isArray(selectedSupplier.products.items)) {
            supplierProductsArray = selectedSupplier.products.items;
          } else if (selectedSupplier?.products && Array.isArray(selectedSupplier.products)) {
            supplierProductsArray = selectedSupplier.products;
          }

          if (supplierProductsArray.length > 0) {
            // Extract products directly from supplier's products array
            const productsFromSupplier = supplierProductsArray.map(sp => sp.product).filter(p => p);
            setSupplierProducts(productsFromSupplier);
            setSupplierProductsData(supplierProductsArray);
          }
        }
      } catch (error) {
        console.error('Failed to load suppliers:', error);
        setApiError('Không thể tải dữ liệu. Vui lòng tải lại trang.');
      }
    };

    loadData();
  }, [isEdit, initialData]);

  // Initialize with one empty item if creating new PO and supplier is selected
  useEffect(() => {
    if (!isEdit && purchaseOrder.items.length === 0 && purchaseOrder.supplier_id) {
      handleAddItem();
    }
  }, [isEdit, purchaseOrder.items.length, purchaseOrder.supplier_id]);

  const handleSupplierChange = useCallback((event, newValue) => {
    const newPO = new PurchaseOrder(purchaseOrder);
    newPO.supplier_id = newValue?.id || null;

    // Clear all items when supplier changes
    newPO.items = [];
    newPO.calculateTotal();

    setPurchaseOrder(newPO);
    setErrors(prev => ({ ...prev, supplier_id: null }));

    // Get products for this supplier - try both structures
    let supplierProductsArray = [];

    if (newValue?.products?.items && Array.isArray(newValue.products.items)) {
      // Wrapped structure from Supplier model
      supplierProductsArray = newValue.products.items;
    } else if (newValue?.products && Array.isArray(newValue.products)) {
      // Direct array structure from API
      supplierProductsArray = newValue.products;
    }

    if (supplierProductsArray.length > 0) {
      // Extract products directly from supplier's products array
      const productsFromSupplier = supplierProductsArray.map(sp => sp.product).filter(p => p);
      setSupplierProducts(productsFromSupplier);
      setSupplierProductsData(supplierProductsArray);

      // Add first item if no items exist
      if (newPO.items.length === 0) {
        newPO.addItem();
      }
    } else {
      setSupplierProducts([]);
      setSupplierProductsData([]);
    }
  }, [purchaseOrder]);

  const handleFieldChange = useCallback((field, value) => {
    const newPO = new PurchaseOrder(purchaseOrder);
    newPO[field] = value;
    setPurchaseOrder(newPO);
    setErrors(prev => ({ ...prev, [field]: null }));
  }, [purchaseOrder]);

  const handleItemChange = useCallback((index, field, value) => {
    const newPO = new PurchaseOrder(purchaseOrder);
    newPO.items[index][field] = value;
    
    // If product_id changed, auto-fill unit_cost from supplier's cost history
    if (field === 'product_id' && value) {
      const supplierProduct = supplierProductsData.find(sp => sp.product?.id === value);
      if (supplierProduct?.cost_history && supplierProduct.cost_history.length > 0) {
        // Get the latest cost (assuming cost_history is sorted by effective_date DESC, id DESC)
        const latestCost = supplierProduct.cost_history[0];
        newPO.items[index].unit_cost = latestCost.cost;
      }
    }
    
    newPO.items[index].calculateLineTotal();
    newPO.calculateTotal();
    setPurchaseOrder(newPO);
    
    // Clear item errors
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors.items && newErrors.items[index]) {
        delete newErrors.items[index][field];
        if (Object.keys(newErrors.items[index]).length === 0) {
          delete newErrors.items[index];
        }
      }
      return newErrors;
    });
  }, [purchaseOrder, supplierProductsData]);

  const handleAddItem = useCallback(() => {
    const newPO = new PurchaseOrder(purchaseOrder);
    newPO.addItem();
    setPurchaseOrder(newPO);
  }, [purchaseOrder]);

  const handleRemoveItem = useCallback((index) => {
    const newPO = new PurchaseOrder(purchaseOrder);
    newPO.removeItem(index);
    setPurchaseOrder(newPO);
  }, [purchaseOrder]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setApiError('');
    
    // Validate form
    const validationErrors = purchaseOrder.getValidationErrors();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      await onSubmit(purchaseOrder.toApiFormat());
    } catch (error) {
      setApiError(error.message || 'Đã xảy ra lỗi khi lưu đơn đặt hàng.');
    } finally {
      setLoading(false);
    }
  };

  const selectedSupplier = suppliers.find(s => s.id === purchaseOrder.supplier_id);

  const brandColors = {
    primary: '#0e2a04',
    secondary: '#6b8e4e',
    accent: '#d4a574',
    light: '#c8d6b8',
    background: '#f5f8f3',
  };

  return (
    <Container component="main" maxWidth="lg">
      <Typography 
        component="h1" 
        variant="h4" 
        sx={{ 
          mb: 4,
          color: brandColors.primary,
          fontWeight: 700,
          fontFamily: 'Poppins, Inter, system-ui, sans-serif',
          textAlign: 'center',
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        {isEdit ? 'Chỉnh sửa đơn đặt hàng' : 'Tạo đơn đặt hàng'}
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {apiError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Paper sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${brandColors.background} 0%, #ffffff  100%)`,
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08), 0 4px 16px rgba(14, 42, 4, 0.04)`,
          border: `1px solid rgba(107, 142, 78, 0.1)`
        }}>
          <Typography variant="h6" sx={{ 
            mb: 3, 
            color: brandColors.primary,
            fontWeight: 600,
            fontFamily: 'Poppins, Inter, system-ui, sans-serif',
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              width: '4px',
              height: '24px',
              backgroundColor: brandColors.primary,
              borderRadius: '2px',
              marginRight: '12px'
            }
          }}>
            Thông tin đơn hàng
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedSupplier || null}
                onChange={handleSupplierChange}
                options={suppliers}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nhà cung cấp *"
                    error={!!errors.supplier_id}
                    helperText={errors.supplier_id}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          borderColor: brandColors.secondary,
                          boxShadow: `0 4px 12px rgba(107, 142, 78, 0.1)`
                        },
                        '&.Mui-focused': {
                          borderColor: brandColors.primary,
                          boxShadow: `0 0 0 3px rgba(14, 42, 4, 0.1)`
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: 500
                      }
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ngày đặt hàng *"
                type="date"
                value={purchaseOrder.order_date}
                onChange={(e) => handleFieldChange('order_date', e.target.value)}
                error={!!errors.order_date}
                helperText={errors.order_date}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: brandColors.secondary,
                      boxShadow: `0 4px 12px rgba(107, 142, 78, 0.1)`
                    },
                    '&.Mui-focused': {
                      borderColor: brandColors.primary,
                      boxShadow: `0 0 0 3px rgba(14, 42, 4, 0.1)`
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontWeight: 500
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ghi chú"
                multiline
                rows={3}
                value={purchaseOrder.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: brandColors.secondary,
                      boxShadow: `0 4px 12px rgba(107, 142, 78, 0.1)`
                    },
                    '&.Mui-focused': {
                      borderColor: brandColors.primary,
                      boxShadow: `0 0 0 3px rgba(14, 42, 4, 0.1)`
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontWeight: 500
                  }
                }}
              />
            </Grid>

            {/* Historical PO checkbox - only show when creating new PO */}
            {!isEdit && (
              <Grid item xs={12}>
                <Box sx={{
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: purchaseOrder.is_historical ? 'rgba(158, 158, 158, 0.1)' : 'transparent',
                  border: purchaseOrder.is_historical ? '1px solid #9e9e9e' : '1px solid transparent',
                  transition: 'all 0.3s ease'
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={purchaseOrder.is_historical}
                        onChange={(e) => handleFieldChange('is_historical', e.target.checked)}
                        sx={{
                          color: brandColors.secondary,
                          '&.Mui-checked': {
                            color: '#9e9e9e',
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{
                          fontWeight: 600,
                          color: brandColors.primary,
                          fontFamily: 'Inter, system-ui, sans-serif'
                        }}>
                          Đơn hàng lịch sử (không ảnh hưởng tồn kho)
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: 'text.secondary',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          mt: 0.5
                        }}>
                          Đơn hàng này chỉ để ghi nhận, không tạo phiếu nhập kho khi nhận hàng
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
            )}

            {/* Show historical badge when editing a historical PO */}
            {isEdit && purchaseOrder.is_historical && (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: 'rgba(158, 158, 158, 0.1)',
                    '& .MuiAlert-icon': {
                      color: '#9e9e9e'
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    Đây là đơn hàng lịch sử
                  </Typography>
                  <Typography variant="body2">
                    Việc nhận hàng sẽ không ảnh hưởng đến số lượng tồn kho hiện tại
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: '16px',
          background: `linear-gradient(135deg, #ffffff 0%, ${brandColors.background} 100%)`,
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08), 0 4px 16px rgba(14, 42, 4, 0.04)`,
          border: `1px solid rgba(107, 142, 78, 0.1)`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ 
              color: brandColors.primary,
              fontWeight: 600,
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                width: '4px',
                height: '24px',
                backgroundColor: brandColors.primary,
                borderRadius: '2px',
                marginRight: '12px'
              }
            }}>
              Chi tiết đơn hàng
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              variant="outlined"
              disabled={!purchaseOrder.supplier_id}
              sx={{
                borderRadius: '12px',
                borderColor: brandColors.secondary,
                color: brandColors.primary,
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 500,
                px: 3,
                py: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: brandColors.primary,
                  backgroundColor: brandColors.light,
                  boxShadow: `0 4px 12px rgba(107, 142, 78, 0.2)`,
                  transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  borderColor: '#ccc',
                  color: '#999',
                }
              }}
            >
              Thêm sản phẩm
            </Button>
          </Box>

          {errors.items && typeof errors.items === 'string' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.items}
            </Alert>
          )}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow sx={{ 
                  background: `linear-gradient(135deg, ${brandColors.background} 0%, #ffffff 100%)`,
                  '& .MuiTableCell-head': {
                    borderBottom: `2px solid ${brandColors.secondary}`,
                    py: 2
                  }
                }}>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>Sản phẩm *</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>Số lượng *</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>Đơn giá *</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>Thành tiền</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: brandColors.primary, 
                    width: 60,
                    fontFamily: 'Poppins, Inter, system-ui, sans-serif',
                    fontSize: '0.95rem'
                  }}>Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrder.items.map((item, index) => {
                  const selectedProduct = supplierProducts.find(p => p.id === item.product_id);
                  const itemErrors = errors.items && errors.items[index];
                  
                  return (
                    <TableRow key={index} sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: 'rgba(245, 248, 243, 0.3)'
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(107, 142, 78, 0.05)',
                        transition: 'background-color 0.2s ease'
                      }
                    }}>
                      <TableCell sx={{ minWidth: isMobile ? 150 : 200 }}>
                        <Autocomplete
                          value={selectedProduct || null}
                          onChange={(event, newValue) => 
                            handleItemChange(index, 'product_id', newValue?.id || null)
                          }
                          options={supplierProducts.filter(product => {
                            // Show the currently selected product for this row
                            if (product.id === item.product_id) return true;
                            // Hide products that are already selected in other rows
                            return !purchaseOrder.items.some((otherItem, otherIndex) => 
                              otherIndex !== index && otherItem.product_id === product.id
                            );
                          })}
                          disabled={!purchaseOrder.supplier_id}
                          getOptionLabel={(option) => option.name || ''}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder={!purchaseOrder.supplier_id ? "Chọn nhà cung cấp trước" : "Chọn sản phẩm"}
                              error={!!(itemErrors && itemErrors.product_id)}
                              helperText={itemErrors && itemErrors.product_id}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  backgroundColor: '#ffffff',
                                  fontSize: '0.875rem',
                                  '&:hover': {
                                    borderColor: brandColors.secondary,
                                    boxShadow: `0 2px 8px rgba(107, 142, 78, 0.1)`
                                  },
                                  '&.Mui-focused': {
                                    borderColor: brandColors.primary,
                                    boxShadow: `0 0 0 2px rgba(14, 42, 4, 0.1)`
                                  }
                                },
                                '& .MuiInputBase-input': {
                                  fontFamily: 'Inter, system-ui, sans-serif'
                                }
                              }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity_ordered}
                          onChange={(e) => 
                            handleItemChange(index, 'quantity_ordered', parseFloat(e.target.value) || 0)
                          }
                          inputProps={{ min: 0, step: 0.01 }}
                          error={!!(itemErrors && itemErrors.quantity_ordered)}
                          helperText={itemErrors && itemErrors.quantity_ordered}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              fontSize: '0.875rem',
                              '&:hover': {
                                borderColor: brandColors.secondary,
                                boxShadow: `0 2px 8px rgba(107, 142, 78, 0.1)`
                              },
                              '&.Mui-focused': {
                                borderColor: brandColors.primary,
                                boxShadow: `0 0 0 2px rgba(14, 42, 4, 0.1)`
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontFamily: 'Inter, system-ui, sans-serif'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.unit_cost}
                          onChange={(e) => 
                            handleItemChange(index, 'unit_cost', parseFloat(e.target.value) || 0)
                          }
                          inputProps={{ min: 0, step: 0.01 }}
                          error={!!(itemErrors && itemErrors.unit_cost)}
                          helperText={itemErrors && itemErrors.unit_cost}
                          placeholder="Tự động điền khi chọn sản phẩm"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              fontSize: '0.875rem',
                              '&:hover': {
                                borderColor: brandColors.secondary,
                                boxShadow: `0 2px 8px rgba(107, 142, 78, 0.1)`
                              },
                              '&.Mui-focused': {
                                borderColor: brandColors.primary,
                                boxShadow: `0 0 0 2px rgba(14, 42, 4, 0.1)`
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontFamily: 'Inter, system-ui, sans-serif'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600,
                          color: brandColors.primary,
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontSize: '0.875rem'
                        }}>
                          {item.line_total.toLocaleString('vi-VN')} đ
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleRemoveItem(index)}
                          disabled={purchaseOrder.items.length === 1}
                          size="small"
                          sx={{
                            color: '#d32f2f',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Typography variant="h6" sx={{ 
              color: brandColors.primary, 
              fontWeight: 600,
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              fontSize: '1.3rem'
            }}>
              Tổng tiền: {purchaseOrder.total_amount.toLocaleString('vi-VN')} đ
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              borderRadius: '12px',
              py: 2,
              px: 6,
              fontSize: '1.1rem',
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              fontWeight: 600,
              boxShadow: `0 8px 24px rgba(14, 42, 4, 0.3)`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: `linear-gradient(135deg, ${brandColors.secondary} 0%, ${brandColors.primary} 100%)`,
                boxShadow: `0 12px 32px rgba(14, 42, 4, 0.4)`,
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: '#ccc',
                boxShadow: 'none'
              }
            }}
          >
            {loading ? 'Đang lưu...' : (isEdit ? 'Cập nhật đơn hàng' : 'Tạo đơn hàng')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default PurchaseOrderForm;